import { Phone, MessageCircle } from "lucide-react";
import { useBusinessConfig } from "@/lib/use-business-config";
import { motion } from "framer-motion";

interface ServiceHeroProps {
  headline: string;
  intro: string;
  image: string;
  imageAlt: string;
  eyebrow?: string;
}

export function ServiceHero({ headline, intro, image, imageAlt, eyebrow = "Service" }: ServiceHeroProps) {
  const biz = useBusinessConfig();

  return (
    <section className="relative py-20 md:py-32 overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={image}
          alt={imageAlt}
          width={1920}
          height={960}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
        />
        {/* Readable directional overlay using design tokens */}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/80 to-foreground/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent" />
      </div>
      <div className="relative mx-auto max-w-6xl px-4 md:px-6">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-[11px] font-bold tracking-[0.28em] uppercase text-primary"
          >
            {eyebrow}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-4 display-lg text-white"
          >
            {headline}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-5 text-base md:text-lg text-white/90 leading-relaxed max-w-lg"
          >
            {intro}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5"
          >
            <a
              href={biz.phoneHref}
              aria-label={`Call Woodseer Autos on ${biz.phone}`}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-bold text-primary-foreground hover:bg-primary/90 transition-all shadow-xl shadow-primary/30 ring-1 ring-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              Call {biz.phone}
            </a>
            <a
              href={biz.whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Message us on WhatsApp"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 border border-white/25 backdrop-blur-sm px-5 py-3.5 text-sm font-bold text-white hover:bg-white/20 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              WhatsApp Us
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
